// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState } from 'react';

import type { WorkCellTaskType } from '@evolvemep/api-ts';
import { ActionIcon, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';

import { useWorkCellSetup } from './WorkCellSetupContext';

type Props = {
  workCellTaskType: WorkCellTaskType;
  onUnmapCompleted: () => void;
};

export const UnmapTaskType = ({ workCellTaskType, onUnmapCompleted }: Props) => {
  const {
    mutationRunning,
    unmapWorkCellTaskType,
    serverSideGridProps: { refreshDetailGrid },
  } = useWorkCellSetup();
  const [opened, setOpened] = useState(false);
  const [deleting, setDeleting] = useState(false);
  return (
    <>
      <ActionIcon
        color={deleting ? 'red' : 'gray'}
        disabled={mutationRunning && !deleting}
        loading={deleting}
        onClick={() => setOpened(true)}
        variant="transparent"
      >
        <EvolveIcon icon="Unpin" color="inheirt" size="sm" className="show-on-row-hover" />
      </ActionIcon>
      <WrappedModal
        title="Unmap task type"
        confirmationText="Unmap"
        buttonColor="red"
        onConfirm={async () => {
          setOpened(false);
          setDeleting(true);
          await unmapWorkCellTaskType(workCellTaskType.workCellTaskTypeId)
            .then(() => {
              notifications.show({
                title: 'Successfully unmapped',
                message: `Unmapped task type ${workCellTaskType.taskTypeName}`,
                color: 'green',
              });
              refreshDetailGrid(workCellTaskType.workCellId, 'Tasks');
              onUnmapCompleted();
            })
            .finally(() => {
              setDeleting(false);
            });
        }}
        onClose={() => setOpened(false)}
        opened={opened}
      >
        <Text>
          Are you sure you want to unmap <b>{workCellTaskType.taskTypeName}</b>?
        </Text>
      </WrappedModal>
    </>
  );
};
